"use client";
import SectionWrapper, {
  basePx,
  SectionWrapperProps,
} from "@cc/section-wrapper";
import {
  Skeleton,
  Tab,
  tabClasses,
  TabProps,
  Tabs,
  tabsClasses,
  TabsProps,
  useScrollTrigger,
} from "@mui/material";
import {
  appbarHeight,
  appbarMotionTransition,
} from "@/layout/header/reactive-appbar";
import { MaterialImage, MaterialImageProps } from "@cc/material-components";
import { Button } from "@theme/components/typography.fontvariant";
import { sxArrayUtil } from "@util/sx-helpers";
import Link from "@cc/link";
import { SetOptional } from "type-fest";
import { findIndex } from "lodash";

interface SecondaryNavigationProps
  extends Omit<SectionWrapperProps, "children"> {
  LogoImageProps?: SetOptional<MaterialImageProps, "width" | "height" | "alt">;
  TabProps?: Omit<TabProps, "children">;
  TabsProps?: Omit<TabsProps, "children">;
  navItems: {
    label: string;
    href: string;
  }[];
  activeNav: string;
}

const SecondaryNavigation = (props: SecondaryNavigationProps) => {
  const {
    LogoImageProps,
    TabProps,
    TabsProps,
    SectionProps = {},
    ContainerProps = {},
    activeNav,
    navItems,
    ...rest
  } = props;
  const { sx, ...restSectionProps } = SectionProps;
  const trigger = useScrollTrigger();

  return (
    <SectionWrapper
      id={"scroll-spy-root"}
      width={1}
      direction={"row"}
      disablePassColorToChildren
      SectionProps={{
        bgcolor: "common.white",
        sx: [
          {
            position: "sticky",
            top: !trigger ? appbarHeight : 0,
            zIndex: 10,
            transition: `all ${appbarMotionTransition.duration}s ease-in-out`,
          },
          ...sxArrayUtil(sx),
        ],
        ...restSectionProps,
      }}
      ContainerProps={{
        py: 0,
        [LogoImageProps ? "pr" : "px"]: { ...basePx, xs: 0, md: 0 },
        alignItems: "center",
        height: 64,
        columnGap: { xs: 3, md: 4 },
        ...ContainerProps,
      }}
      {...rest}
    >
      {LogoImageProps && (
        <MaterialImage
          width={124}
          height={150}
          alt={"company-logo"}
          {...LogoImageProps}
          sx={[
            {
              width: { xs: 94, md: 124 },
              height: "100%",
              objectFit: "contain",
            },
            ...sxArrayUtil(LogoImageProps?.sx),
          ]}
        />
      )}
      {navItems.length > 0 ? (
        <Tabs
          value={findIndex(navItems, (item) => item.href.endsWith(activeNav))}
          variant={"scrollable"}
          scrollButtons={false}
          {...TabsProps}
          sx={[
            {
              width: 1,
              color: "grey.600",
              [`.${tabsClasses.flexContainer}`]: {
                columnGap: 4,
              },
            },
            ...sxArrayUtil(TabsProps?.sx),
          ]}
        >
          {navItems.map((item, index) => {
            const { label, href } = item;
            return (
              <Tab
                key={`${href}-${index}`}
                component={Link}
                label={label}
                href={href}
                disableRipple
                {...TabProps}
                sx={[
                  {
                    border: "none",
                    fontSize: Button,
                    px: 2,
                    height: { xs: 56, md: 64 },
                    color: "inherit",
                    fontWeight: 400,
                    opacity: 0.6,
                    [`&.${tabClasses.selected}`]: {
                      color: "primary.main",
                      opacity: 1,
                    },
                  },
                  ...sxArrayUtil(TabProps?.sx),
                ]}
              />
            );
          })}
        </Tabs>
      ) : (
        <Skeleton
          variant={"rectangular"}
          sx={{
            bgcolor: "secondary.800",
            height: { xs: 56, md: 64 },
            width: 1,
          }}
        />
      )}
    </SectionWrapper>
  );
};

export default SecondaryNavigation;
